import React, { useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  DeleteOutline,
  MarkEmailUnread,
  Drafts,
  AttachFile,
  Close
} from '@mui/icons-material';
import { MailMessageDetail } from '../types';
import { MAIL_COLORS } from '../constants';
import { formatMailFullDate, getMailInitials, getAvatarColor } from '../utils';
import MailAttachmentItem from './MailAttachmentItem';

interface MailViewerProps {
  message: MailMessageDetail | null;
  username: string;
  isLoading: boolean;
  onMarkUnread: (id: string) => void;
  onDeleteMessage: (id: string) => void;
  onClose?: () => void;
}

export const MailViewer: React.FC<MailViewerProps> = ({
  message,
  username,
  isLoading,
  onMarkUnread,
  onDeleteMessage,
  onClose
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Автоматическая подгонка высоты iframe под содержимое письма
  useEffect(() => {
    if (!iframeRef.current || !message?.html) return;
    const iframe = iframeRef.current;
    const handleLoad = () => {
      try {
        if (iframe.contentWindow?.document?.body) {
          const docHeight = iframe.contentWindow.document.body.scrollHeight;
          iframe.style.height = `${Math.max(docHeight + 40, 300)}px`;
        }
      } catch {
        iframe.style.height = '600px';
      }
    };
    iframe.addEventListener('load', handleLoad);
    return () => iframe.removeEventListener('load', handleLoad);
  }, [message?.html]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          py: 12,
          gap: 2
        }}
      >
        <CircularProgress size={32} sx={{ color: MAIL_COLORS.primary }} />
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          Загрузка содержимого письма...
        </Typography>
      </Box>
    );
  }

  if (!message) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          py: 12,
          px: 3,
          color: '#94a3b8',
          textAlign: 'center',
          gap: 1.5
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '20px',
            bgcolor: '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8'
          }}
        >
          <Drafts sx={{ fontSize: 32 }} />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#475569' }}>
          Выберите письмо
        </Typography>
        <Typography variant="body2" sx={{ maxWidth: 280 }}>
          Выберите сообщение из списка слева, чтобы прочитать его и просмотреть вложения
        </Typography>
      </Box>
    );
  }

  const senderName = message.from?.name || message.from?.email || 'Неизвестный отправитель';
  const initials = getMailInitials(message.from?.name, message.from?.email);
  const avatarColor = getAvatarColor(message.from?.email || senderName);
  const formattedFullDate = formatMailFullDate(message.date);

  // Подготовка безопасного HTML документа для iframe
  const iframeContent = message.html
    ? `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              font-size: 14px;
              line-height: 1.6;
              color: #1e293b;
              margin: 16px;
              word-break: break-word;
            }
            img {
              max-width: 100%;
              height: auto;
            }
            table {
              max-width: 100%;
            }
            a {
              color: #2563eb;
              text-decoration: underline;
            }
          </style>
        </head>
        <body>${message.html}</body>
      </html>
    `
    : '';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#ffffff',
        overflowY: 'auto'
      }}
    >
      {/* 1. Верхняя панель действий */}
      <Box
        sx={{
          p: 2,
          px: { xs: 2, sm: 3 },
          borderBottom: `1px solid ${MAIL_COLORS.borderLight}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          position: 'sticky',
          top: 0,
          bgcolor: '#ffffff',
          zIndex: 5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Пометить как непрочитанное">
            <IconButton
              size="small"
              onClick={() => onMarkUnread(message.id)}
              sx={{ color: '#64748b', '&:hover': { color: '#1e293b', bgcolor: '#f1f5f9' } }}
            >
              <MarkEmailUnread sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Удалить в корзину">
            <IconButton
              size="small"
              onClick={() => onDeleteMessage(message.id)}
              sx={{ color: '#64748b', '&:hover': { color: '#ef4444', bgcolor: '#fef2f2' } }}
            >
              <DeleteOutline sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ color: '#64748b' }}>
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>

      {/* 2. Заголовок письма и данные об отправителе */}
      <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#0f172a',
            fontSize: '1.25rem',
            lineHeight: 1.35,
            mb: 2.5
          }}
        >
          {message.subject || '(Без темы)'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              fontSize: '0.95rem',
              fontWeight: 700,
              bgcolor: avatarColor,
              color: '#ffffff',
              flexShrink: 0
            }}
          >
            {initials}
          </Avatar>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 1
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: '#1e293b'
                }}
              >
                {senderName}
              </Typography>

              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                {formattedFullDate}
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.84rem', mt: 0.2 }}>
              {message.from?.email}
            </Typography>

            {message.to && message.to.length > 0 && (
              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.76rem', display: 'block', mt: 0.5 }}>
                Кому: {message.to.map(t => t.name || t.email).join(', ')}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: MAIL_COLORS.borderLight }} />

      {/* 3. Список прикрепленных файлов (если есть) */}
      {message.attachments && message.attachments.length > 0 && (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f8fafc', borderBottom: `1px solid ${MAIL_COLORS.borderLight}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <AttachFile sx={{ fontSize: 18, color: '#64748b', transform: 'rotate(45deg)' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', fontSize: '0.85rem' }}>
              Вложения ({message.attachments.length})
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {message.attachments.map((att) => (
              <MailAttachmentItem
                key={att.part}
                attachment={att}
                username={username}
                messageId={message.id}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* 4. Тело письма */}
      <Box sx={{ p: { xs: 2, sm: 3 }, flexGrow: 1 }}>
        {message.html ? (
          <iframe
            ref={iframeRef}
            title="email-body"
            srcDoc={iframeContent}
            sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
            style={{
              width: '100%',
              border: 'none',
              display: 'block',
              minHeight: 300
            }}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: '#334155',
              fontSize: '0.92rem',
              lineHeight: 1.6
            }}
          >
            {message.text || 'Письмо не содержит текста.'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MailViewer;
